import { analytics } from '@/features/analytics/events'
import { isCaptchaFailedError } from '@/features/turnstile'
import { t } from '@lingui/core/macro'
import type { AxiosRequestConfig } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { nectarApi } from '../api/client'
import type {
  AssessmentProgress,
  CefrLevel,
  NectarTask,
  PaginatedResponse,
  ProficiencyAssessmentFinalResult
} from '../api/models'
import {
  completeAssessment,
  createAssessment,
  getAssessment,
  getAssessmentsByLanguage
} from '../api/proficiency-assessment'

export type AssessmentPhase =
  | 'loading'
  | 'setup'
  | 'claim-reward'
  | 'text-phase'
  | 'speech-phase'
  | 'completing'
  | 'results'
  | 'already-assessed'
  | 'expired'

const MAX_POLL_RETRIES = 30
const POLL_INTERVAL_MS = 3000
const POLL_INITIAL_DELAY_MS = 2000

type UseProficiencyAssessmentOptions = {
  projectId?: string
  language: string
  captchaEnabled: boolean
  getSubmissionToken: () => string | null
  resetSubmissionWidget: () => void
  onBack: () => void
  onCompleted: () => void
}

type CallbackOptions = {
  onCaptchaFailed?: () => void
}

export function useProficiencyAssessment({
  projectId,
  language,
  captchaEnabled,
  getSubmissionToken,
  resetSubmissionWidget,
  onBack,
  onCompleted
}: UseProficiencyAssessmentOptions) {
  const [phase, setPhase] = useState<AssessmentPhase>('loading')
  const [assessmentId, setAssessmentId] = useState<string | null>(null)
  const [taskId, setTaskId] = useState<string | undefined>(undefined)
  const taskIdRef = useRef<string | null>(null)
  // Single write point keeps the ref (sync reads in async closures) and the
  // state (drives useTaskHeartbeat) from ever diverging.
  const assignTaskId = (id: string | undefined) => {
    taskIdRef.current = id ?? null
    setTaskId(id)
  }
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [finalResult, setFinalResult] = useState<ProficiencyAssessmentFinalResult | null>(null)
  const [progress, setProgress] = useState<AssessmentProgress | null>(null)
  const mountedRef = useRef(true)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const submitTask = useCallback(
    async (tid: string, aid: string, turnstileToken?: string) => {
      const config: AxiosRequestConfig | undefined = turnstileToken
        ? { headers: { 'X-Turnstile-Token': turnstileToken } }
        : undefined
      await nectarApi.post(`/v1/projects/${projectId}/tasks/${tid}/submissions`, { assessmentId: aid }, config)
    },
    [projectId]
  )

  const pollForResult = useCallback(
    (id: string) => {
      let retries = 0
      const poll = async () => {
        if (!mountedRef.current) return
        retries++
        if (retries > MAX_POLL_RETRIES) {
          toast.error(t`Evaluation timed out. Check back later.`)
          onBack()
          return
        }
        try {
          const data = await getAssessment(id)
          if (!mountedRef.current) return
          if (data.status === 'COMPLETED' && data.finalResult) {
            setFinalResult(data.finalResult)
            if (!taskIdRef.current) {
              setPhase('results')
              onCompleted()
              return
            }
            if (captchaEnabled) {
              const token = getSubmissionToken()
              if (!token) {
                toast.error(t`Please claim your reward to finish.`)
                setPhase('claim-reward')
                return
              }
              try {
                await submitTask(taskIdRef.current, id, token)
                resetSubmissionWidget()
                setPhase('results')
                onCompleted()
              } catch (err) {
                resetSubmissionWidget()
                if (isCaptchaFailedError(err)) {
                  toast.error(t`Verification failed. Please try again.`)
                } else {
                  toast.error(t`Failed to submit task`)
                }
                setPhase('claim-reward')
              }
              return
            }
            setPhase('results')
            try {
              await submitTask(taskIdRef.current, id)
              onCompleted()
            } catch {
              toast.error(t`Failed to submit task`)
            }
            return
          }
          if (data.status === 'FAILED') {
            toast.error(t`Assessment evaluation failed`)
            onBack()
            return
          }
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS)
        } catch {
          toast.error(t`Failed to check status`)
          onBack()
        }
      }
      pollTimeoutRef.current = setTimeout(poll, POLL_INITIAL_DELAY_MS)
    },
    [onBack, submitTask, onCompleted, captchaEnabled, getSubmissionToken, resetSubmissionWidget]
  )

  // Initialize: check existing assessments for this language
  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    const init = async () => {
      try {
        let taskData = { data: [] as NectarTask[] }
        if (projectId) {
          // 1. Fetch the single assigned task
          taskData = await nectarApi.get<PaginatedResponse<NectarTask>>(`/v1/projects/${projectId}/tasks`, {
            page: 1,
            limit: 1
          })
        }
        const task = taskData.data[0]
        if (!task && !!projectId) {
          toast.error(t`No task assigned`)
          onBack()
          return
        }

        assignTaskId(task?.id)

        // 2. Fetch all assessments for this language
        const assessments = await getAssessmentsByLanguage(language)
        const allAssessments = assessments.data

        // Already submitted → load result for display
        if (task?.submissionCount > 0) {
          const completedMeta = allAssessments.find((a) => a.status === 'COMPLETED')
          if (completedMeta) {
            const detail = await getAssessment(completedMeta.id)
            if (detail.finalResult) {
              setFinalResult(detail.finalResult)
              setAssessmentId(detail.id)
            }
          }
          setPhase('results')
          return
        }

        const inProgress = allAssessments.find((a) => a.status === 'IN_PROGRESS')
        if (inProgress) {
          setAssessmentId(inProgress.id)
          setExpiresAt(inProgress.expiresAt)

          if (inProgress.expiresAt && new Date(inProgress.expiresAt).getTime() <= Date.now()) {
            setPhase('expired')
            return
          }

          const detail = await getAssessment(inProgress.id)
          setProgress(detail.progress)

          if (detail.status === 'COMPLETED' && detail.finalResult) {
            setFinalResult(detail.finalResult)
            setAssessmentId(detail.id)
            setPhase('claim-reward')
          } else if (detail.progress.currentPhase === 'speech') {
            setPhase('speech-phase')
          } else if (detail.progress.currentPhase === 'completing' || detail.progress.currentPhase === 'completed') {
            setPhase('completing')
          } else {
            setPhase('text-phase')
          }
          return
        }

        // 4. Check for COMPLETED + uncredited → claim reward
        const completed = allAssessments.find((a) => a.status === 'COMPLETED')
        if (completed) {
          const detail = await getAssessment(completed.id)
          if (detail.finalResult) {
            setFinalResult(detail.finalResult)
            setAssessmentId(detail.id)
            setPhase('claim-reward')
            return
          }
        }

        // 5. No usable assessment → setup
        setPhase('setup')
      } catch {
        toast.error(t`Failed to load assessment`)
        onBack()
      }
    }

    init()
  }, [projectId, language, onBack])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (phase === 'loading') return
    analytics.assessmentPhase({ projectId, phase })
  }, [phase, projectId])

  useEffect(() => {
    if (finalResult) analytics.assessmentCompleted({ projectId, level: finalResult.level })
  }, [finalResult, projectId])

  const startAssessment = useCallback(
    async (selfLevel: CefrLevel, turnstileToken?: string, callbacks?: CallbackOptions) => {
      setStarting(true)
      try {
        const data = await createAssessment({ selfAssessedLevel: selfLevel, language }, turnstileToken)
        setAssessmentId(data.id)
        setExpiresAt(data.expiresAt)
        setProgress(data.progress)
        setPhase('text-phase')
      } catch (err) {
        if (isCaptchaFailedError(err)) {
          toast.error(t`Verification failed. Please try again.`)
          callbacks?.onCaptchaFailed?.()
        } else if ((err as { response?: { status?: number } }).response?.status === 409) {
          setPhase('already-assessed')
        } else {
          toast.error(t`Failed to start assessment`)
        }
      } finally {
        setStarting(false)
      }
    },
    [language]
  )

  const claimReward = useCallback(
    async (turnstileToken?: string, callbacks?: CallbackOptions) => {
      if (!assessmentId || !taskIdRef.current) return
      setClaiming(true)
      try {
        await submitTask(taskIdRef.current, assessmentId, turnstileToken)
        setPhase('results')
        onCompleted()
      } catch (err) {
        if (isCaptchaFailedError(err)) {
          toast.error(t`Verification failed. Please try again.`)
          callbacks?.onCaptchaFailed?.()
          return
        }
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
            : ''

        if (msg.includes('already been credited')) {
          toast.error(t`This assessment has already been credited to another task.`)
          setPhase('already-assessed')
        } else {
          toast.error(t`Failed to claim reward`)
        }
      } finally {
        setClaiming(false)
      }
    },
    [assessmentId, submitTask, onCompleted]
  )

  const handleTextCompleted = useCallback(() => {
    setPhase('speech-phase')
  }, [])

  const handleAllRecorded = useCallback(async () => {
    if (!assessmentId) return
    setPhase('completing')
    try {
      await completeAssessment(assessmentId)
      pollForResult(assessmentId)
    } catch {
      toast.error(t`Failed to submit for evaluation`)
      setPhase('speech-phase')
    }
  }, [assessmentId, pollForResult])

  const handleExpired = useCallback(() => {
    setPhase((prev) => (prev === 'text-phase' || prev === 'speech-phase' || prev === 'completing' ? 'expired' : prev))
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  return {
    phase,
    assessmentId,
    taskId,
    expiresAt,
    finalResult,
    starting,
    claiming,
    progress,
    setProgress,
    startAssessment,
    claimReward,
    handleTextCompleted,
    handleAllRecorded,
    handleExpired
  }
}
